import React, {useEffect, useState} from 'react';
import {Badge, Button, Dropdown, Form, Input, Modal, Tree} from 'antd';
import CategoryTreeModal from './CategoryTreeModal';
import type {DataNode} from 'antd/es/tree';
import './CategoryTree.css';
import {
  DeleteOutlined,
  EditOutlined,
  FolderOutlined,
  MoreOutlined,
  PlusOutlined,
  SwapOutlined
} from '@ant-design/icons';
import {useCategories} from '../hooks/useCategories';
import type {Key} from 'rc-tree/lib/interface';
import type {Category} from '../types/category';

import {useCategoryOperations} from '../hooks/useCategoryOperations';
import CategoryTreeHeader from "./CategoryTreeHeader";
import {ALL_NOTES, ROOT_LEVEL} from "../utils/constants";

export interface NodeType {
  type: string;
  id: number;
  name: string;
}


interface CategoryTreeProps {
  showActions?: boolean;
  selectedCategoryId: number;
  filterOutCategory?: number;
  onSelectCategory?: (categoryId: number, categoryBreadcrumb: string) => void;
  isMoveCategoryModal?: boolean;
  isMoveNoteModal?: boolean;
}

interface CategoryDataNode extends DataNode {
  className?: string;
  title: React.ReactNode;
  key: string;
  children?: CategoryDataNode[];
  data?: Category;
}

const CategoryTree: React.FC<CategoryTreeProps> = ({
  showActions = true,
  selectedCategoryId,
  filterOutCategory,
  onSelectCategory,
  isMoveCategoryModal = false,
  isMoveNoteModal = false,
}) => {


  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [currentSelectedCategoryId, setCurrentSelectedCategoryId] = useState<number>(selectedCategoryId);

  const handleCategoryTreeHeaderNodeSelection = (node: NodeType) => {
    setCurrentSelectedCategoryId(node.id);
    onSelectCategory?.(node.id, '');
  };

  // Fetch categories using the useCategories hook
  const { categories, loading } = useCategories();

  // Notify parent when categories are loaded and handle initial selection
  useEffect(() => {
    if (!loading) {
      setCurrentSelectedCategoryId(selectedCategoryId)
      // If there's a selected category, expand its parent nodes
      if (selectedCategoryId && selectedCategoryId !== ALL_NOTES.id && selectedCategoryId !== ROOT_LEVEL.id) {
        const parentIds: string[] = [];
        let category = categories.find(cat => cat.id === selectedCategoryId);

        // Build list of parent IDs
        while (category) {
          const parent = categories.find(cat => cat.id === category?.parent_id);
          if (!parent) break;
          parentIds.unshift(parent.id.toString());
          category = parent;
        }

        // Merge new parent IDs with existing expanded keys
        setExpandedKeys(prevKeys => Array.from(new Set([...prevKeys, ...parentIds])));
        onSelect([selectedCategoryId]);
      }
    }
  }, [categories, loading, selectedCategoryId]);

  // Update form values when editingCategory changes
  useEffect(() => {
    if (editingCategory) {
      form.setFieldsValue({
        name: editingCategory.name
      });
    } else {
      form.resetFields();
    }
  }, [editingCategory, form]);

  // Use category operations hook
  const {
    handleCreateCategory: createCategoryOp,
    handleUpdateCategory: updateCategoryOp,
    handleDeleteCategory: deleteCategoryOp,
    handleMoveCategory: moveCategoryOp
  } = useCategoryOperations();

  // Handle category operations
  const handleCreateCategory = async (name: string) => {
    try {
      await createCategoryOp(name, selectedParentId ?? undefined);
      setIsModalVisible(false);
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const handleUpdateCategory = async (name: string) => {
    if (!editingCategory) return;
    try {
      await updateCategoryOp(editingCategory.id, name, editingCategory.parent_id);
      setIsModalVisible(false);
      setEditingCategory(null);
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const handleDeleteCategory = async (category: Category) => {
    try {
      await deleteCategoryOp(category.id);
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const handleMoveCategory = async (categoryId: number, newParentId: number) => {
    try {
      await moveCategoryOp(categoryId, newParentId);
      setIsMoveModalVisible(false);
      onSelect([categoryId.toString()]);
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  // Modal handlers
  const openCreateModal = (parentId: number | null = null) => {
    setSelectedParentId(parentId);
    setModalMode('create');
    setIsModalVisible(true);
    setEditingCategory(null);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setModalMode('edit');
    setIsModalVisible(true);
  };

  // Get breadcrumb string for a category
  const getBreadcrumbString = (categoryId: string): string => {
    const category = categories.find(cat => cat.id.toString() === categoryId);
    if (!category) return '';

    const breadcrumb: Category[] = [category];
    let currentCategory: Category | undefined = category;

    // Build the breadcrumb by traversing up through parents
    while (currentCategory?.parent_id) {
      const parent = categories.find(cat => cat.id === currentCategory?.parent_id);
      if (!parent) break;
      breadcrumb.unshift(parent);
      currentCategory = parent;
    }

    // Join category names with ' > ' to create the breadcrumb string
    return breadcrumb.map(c => c.name).join(' > ');
  };

  // Get all descendant IDs of a category (including itself)
  const getDescendantIds = (categoryId: number, allCategories: Category[]): number[] => {
    const descendants = [categoryId];
    const children = allCategories.filter(cat => cat.parent_id === categoryId);

    children.forEach(child => {
      descendants.push(...getDescendantIds(child.id, allCategories));
    });

    return descendants;
  };

  const openMoveModal = (category: Category) => {
    setEditingCategory(category);
    setIsMoveModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingCategory(null);
    setSelectedParentId(null);
  };

  const handleMoveModalCancel = () => {
    setIsMoveModalVisible(false);
    setEditingCategory(null);
  };

  // Tree handlers
  const onSelect = (selectedKeys: Key[]) => {
    const categoryId = selectedKeys[0]?.toString();
    if (categoryId) {
      let currentCategoryId: number = parseInt(categoryId);
      setCurrentSelectedCategoryId(currentCategoryId);
      if (isMoveCategoryModal) {
        onSelectCategory?.(currentCategoryId, '');
      } else {
        const breadcrumbString = getBreadcrumbString(categoryId);
        onSelectCategory?.(currentCategoryId, breadcrumbString);
      }
    }
  };

  const onExpand = (expandedKeys: Key[]) => {
    setExpandedKeys(expandedKeys.map(key => key.toString()));

  };

  // Build tree data
  const handlers = {
    openCreateModal,
    openEditModal,
    openMoveModal,
    handleDeleteCategory,
  };

  const treeData = React.useMemo(() => {
    return buildTreeData(categories, handlers, showActions, filterOutCategory);
  }, [categories, handlers, showActions, filterOutCategory]);

  return (
    <div className="w-full">
      {onSelectCategory && (
        <CategoryTreeHeader
          selectedCategoryId={currentSelectedCategoryId}
          openCreateModal={() => openCreateModal(null)}
          onClickOnHeaderNode={handleCategoryTreeHeaderNodeSelection}
          isMoveCategoryModal={isMoveCategoryModal}
          isMoveNoteModal={isMoveNoteModal}
        />
      )}

      <Tree
        className="w-full"
        treeData={treeData}
        selectedKeys={currentSelectedCategoryId ? [currentSelectedCategoryId.toString()]: []}
        expandedKeys={expandedKeys}
        onSelect={onSelect}
        blockNode
        onExpand={onExpand}
        showLine={false}
        selectable
      />

      <Modal
        title={modalMode === 'create' ? 'Create Category' : 'Edit Category'}
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
      >
        <Form
          form={form}
          onFinish={values => {
            if (modalMode === 'create') {
              handleCreateCategory(values.name);
            } else {
              handleUpdateCategory(values.name);
            }
          }}
        >
          <Form.Item
            name="name"
            rules={[{ required: true, message: 'Please input category name!' }]}
          >
            <Input placeholder="Category name" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" className="bg-[#048ba8] hover:bg-[#0b1d51] border-none">
              {modalMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <CategoryTreeModal
        isOpen={isMoveModalVisible}
        onClose={handleMoveModalCancel}
        onSelect={(targetId) => {
          if (editingCategory && targetId !== editingCategory.id) {
            handleMoveCategory(editingCategory.id, targetId);
            handleMoveModalCancel();
          }
        }}
        selectedCategoryId={selectedCategoryId}
      />
    </div>
  );

}


const getChildrenIds = (categories: Category[], categoryId: number): number[] => {
  const children: number[] = [];
  const queue = [categoryId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    children.push(currentId);
    categories
      .filter(cat => cat.parent_id === currentId)
      .forEach(cat => queue.push(cat.id));
  }

  return children;
};

const buildTreeData = (
  categories: Category[],
  handlers: {
    openCreateModal: (parentId?: number | null) => void;
    openEditModal: (category: Category) => void;
    openMoveModal: (category: Category) => void;
    handleDeleteCategory: (category: Category) => Promise<void>;
  },
  showActions: boolean,
  filterOutCategory?: number
): CategoryDataNode[] => {
  // First pass: create all nodes
  const categoryMap = new Map<number, CategoryDataNode>();
  const rootNodes: CategoryDataNode[] = [];

  // Get excluded category IDs when in move modal
  const excludedIds = filterOutCategory ? getChildrenIds(categories, filterOutCategory) : [];

  // Create nodes for all categories (excluding selected category and its children in move modal)
  categories.forEach((category) => {
    // Skip if this category should be excluded
    if (excludedIds.includes(category.id)) return;
    const node: CategoryDataNode = {
      title: (
        <div className="flex items-center justify-between w-full">
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <FolderOutlined className="text-[#048ba8] flex-shrink-0" />
            <span className="truncate">{category.name}</span>
          </span>
          {showActions && (
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: '1',
                    label: 'Add Subcategory',
                    icon: <PlusOutlined />,
                    onClick: () => {
                      handlers.openCreateModal(category.id);
                    }
                  },
                  {
                    key: '2',
                    label: 'Edit',
                    icon: <EditOutlined />,
                    onClick: () => {
                      handlers.openEditModal(category);
                    }
                  },
                  {
                    key: '3',
                    label: 'Move',
                    icon: <SwapOutlined />,
                    onClick: () => {
                      handlers.openMoveModal(category);
                    }
                  },
                  {
                    type: 'divider'
                  },
                  {
                    key: '4',
                    label: 'Delete',
                    icon: <DeleteOutlined className="text-[#d64933]" />,
                    className: 'text-[#d64933]',
                    onClick: () => {
                      handlers.handleDeleteCategory(category);
                    }
                  },
                ],
              }}

            >
              <Badge count={0} dot offset={[-8, 0]}>
                <MoreOutlined className="text-[#849469] hover:text-[#0b1d51] cursor-pointer" />
              </Badge>
            </Dropdown>
          )}
        </div>
      ),
      key: category.id.toString(),
      data: category,
      children: [],
    };

    categoryMap.set(category.id, node);
  });

  // Sort categories to ensure parent nodes are processed first
  const sortedCategories = [...categories].sort((a, b) => {
    if (!a.parent_id && b.parent_id) return -1;
    if (a.parent_id && !b.parent_id) return 1;
    return 0;
  });

  // Second pass: build the tree structure
  sortedCategories.forEach((category) => {
    const node = categoryMap.get(category.id);
    if (!node) return;

    if (!category.parent_id) {
      rootNodes.push(node);
    } else {
      const parentNode = categoryMap.get(category.parent_id);
      if (parentNode && parentNode.children) {
        parentNode.children.push(node);
      }
    }
  });

  return rootNodes;
};

export default CategoryTree;
